import storeAxios from "@/store/axios";
import { calculationDataProcessor, calculatorHelpers } from "@/helpers";
import { deepClone } from "@/utils";

const {
    patchSpanRestraints,
    patchDeformationLimit
} = calculatorHelpers;

export default {
    namespaced: true,

    state: {
        isSpanRestraintsManuallyChanged: false,
        rawCalculationId: null,
        defaultCalculation: null,
        calculation: null
    },

    mutations: {
        setIsSpanRestraintsManuallyChanged(state, { val }) {
            state.isSpanRestraintsManuallyChanged = val;
        },

        setCalculation(state, { val }) {
            state.calculation = val;
        },

        setRawCalculationId(state, { val }) {
            state.rawCalculationId = val;
        },

        setDefaultCalculation(state, { val }) {
            state.defaultCalculation = val;
        }
    },

    actions: {
        async boot({ commit, state, dispatch }, rawCalculation) {
            if (state.calculation) {
                dispatch('destroy')
            }

            let calculation;

            if (rawCalculation) {
                commit('setRawCalculationId', { val: rawCalculation.id });
                calculation = await dispatch('getCalculationFromRawCalculation', rawCalculation);
            } else {
                calculation = await dispatch('getDefaultCalculation');
            }

            commit('setCalculation', { val: calculation });
        },

        submit({ state, commit, dispatch, getters, rootState }) {
            state.calculation.Date = new Date().toISOString();
            state.calculation.input.lang = rootState.selectedLang;

            return new Promise((resolve, reject) => {
                calculationDataProcessor.toServer(
                    getters.userData,
                    rootState.calculationData,
                    state.calculation,
                    (err, rawCalculation) => {

                        if (err) {
                            //we dont care about result
                            storeAxios.post("Calculations", rawCalculation);
                            reject({ message: err });
                        } else {
                            resolve(rawCalculation);
                        }
                    }
                );
            })
                .then(rawCalculation => {
                    return dispatch('createUsersCalculation', rawCalculation, {
                        root: true
                    })
                })
                .then(res => {
                    commit('setRawCalculationId', { val: res.id });
                    return res;
                })
        },

        async getDefaultCalculation({ commit, state, getters, dispatch }, { type } = { type: 1 }) {
            if (!state.defaultCalculation) {

                const calculationData = await dispatch('fetchCalculationData', null, { root: true })

                // dumb default
                const defaultCalculation = await new Promise((resolve, reject) => {
                    calculationDataProcessor.fromServer(
                        getters.userData,
                        calculationData,
                        (err, processedCalculation) => {
                            if (err) {
                                //we dont care about result
                                storeAxios.post("Calculation", processedCalculation);

                                reject({ message: err });

                            } else {
                                resolve(processedCalculation);
                            }
                        }
                    )
                });

                commit('setDefaultCalculation', { val: defaultCalculation });
            }

            // specify type defaults
            // why not do them in the same place? Good question.
            const defaultCalculationCopy = deepClone(state.defaultCalculation);

            const { input } = defaultCalculationCopy;

            input.type = type;

            input.spanRestraints = patchSpanRestraints(input.spanRestraints, {
                type: input.type,
                format: input.format,
                modelSpans: input.modelSpans,
                hasCantilever: input.hasCantilever,
                direction: input.direction,
            });

            input.deformation.limit = patchDeformationLimit(input.deformation.limit, {
                type: input.type
            })

            return defaultCalculationCopy;
        },

        async getCalculationFromRawCalculation({ getters, dispatch }, rawCalculation) {
            const calculationData = await dispatch('fetchCalculationData', null, { root: true })

            const calculation = await new Promise((resolve, reject) => {
                calculationDataProcessor.fromServer(
                    getters.userData,
                    calculationData,
                    rawCalculation,
                    (err, processedCalculation) => {
                        if (err) {
                            //we dont care about result
                            storeAxios.post("Calculations", processedCalculation);
                            reject({ message: err });

                        } else {
                            resolve(processedCalculation);
                        }
                    }
                );
            });
            return calculation;
        },

        destroy({ commit }) {
            commit('setIsSpanRestraintsManuallyChanged', { val: false })
            commit('setRawCalculationId', { val: null });
            commit('setCalculation', { val: null });
        }
    },

    getters: {
        userData(state, getters, rootState) {
            return {
                lang: rootState.selectedLang,
                ...rootState.user
            };
        },

        load(state) {
            return (_case, _type, _limit = null) => {
                return state.calculation.input.loads.find(load => {
                    return load.case === _case && load.type === _type && (_limit === null ? true : _limit === load.limit);
                });
            }
        },

        totalSpans(state) {
            return state.calculation.input.modelSpans.length;
        },

        modelSpansLength(state) {
            return +(state.calculation.input.modelSpans.reduce((result, val) => result + val, 0)).toFixed(2)
        },

        isNaturalElementsValid(state, getters) {
            return (_type, _case) => {

                const loads = getters.load(_type, _case).value;

                const loadsValue = loads[0][0] + loads[1][0] + loads[2][0];

                if (loadsValue === 0) {
                    return true;
                }

                const loadsLength = loads[0][1] + loads[1][1] + loads[2][1];

                return Math.abs(loadsLength - getters.modelSpansLength) < 1E-5;
            }
        },

        isWindLocalPressureValid(state, getters) {
            return getters.isNaturalElementsValid('windLocal', 'pressure');
        },

        isWindLocalSuctionValid(state, getters) {
            return getters.isNaturalElementsValid('windLocal', 'suction');
        },

        isWindPressureValid(state, getters) {
            return getters.isNaturalElementsValid('wind', 'pressure');
        },

        isWindSuctionValid(state, getters) {
            return getters.isNaturalElementsValid('wind', 'suction');
        },


        isSnowAccumulationValid(state, getters) {
            return getters.isNaturalElementsValid('snow', 'accumulation');
        },

        isMinMaxValid(state, getters) {
            const internal = state.calculation.input.internal;

            return (_case, _type) => {
                if (internal.loads[_type][_case].isMinMaxModeOn) {
                    const minLoads = getters.load(_case, _type, 'min');
                    const maxLoads = getters.load(_case, _type, 'max');

                    return !minLoads.value.some((minVal, i) => {
                        const maxVal = maxLoads.value[i];
                        return minVal > maxVal;
                    });

                } else {
                    return true;
                }

            }
        },

        hasUserInput(state, getters, rootState) {
            return calculationDataProcessor.hasValues(rootState.calculationData, state.calculation);
        }
    }

}
